import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import { apiClient } from '@/lib/api-client';
import { ClientSearchModal, type ClientSearchResult } from '@/components/ClientSearchModal';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ShareDocumentsModalProps {
  open: boolean;
  onClose: () => void;
  documentIds: string[];
  documentNames: string[];
}

interface ShareResult {
  shareBatchId: string;
  recipientCount: number;
  shareCount: number;
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Modal for sharing selected documents with clients via public access links.
 * Uses ClientSearchModal for recipient selection with filtering and pagination.
 */
export function ShareDocumentsModal({
  open,
  onClose,
  documentIds,
  documentNames,
}: ShareDocumentsModalProps) {
  const { toast } = useToast();

  const [selectedClients, setSelectedClients] = useState<ClientSearchResult[]>([]);
  const [isClientSearchOpen, setIsClientSearchOpen] = useState(false);
  const [expiryDays, setExpiryDays] = useState(7);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<ShareResult | null>(null);

  const handleClientConfirm = useCallback((clients: ClientSearchResult[]) => {
    setSelectedClients(clients);
    setIsClientSearchOpen(false);
  }, []);

  const removeClient = useCallback((clientId: string) => {
    setSelectedClients((prev) => prev.filter((c) => c.id !== clientId));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (selectedClients.length === 0 || documentIds.length === 0) return;

    setIsSubmitting(true);
    try {
      const response = await apiClient.post<{
        success: boolean;
        data: { shareBatchId: string; shares: unknown[]; recipientCount: number };
      }>('/documents/share', {
        document_ids: documentIds,
        recipient_contact_ids: selectedClients.map((c) => c.id),
        expiry_days: expiryDays,
        message: message.trim() || null,
      });

      setResult({
        shareBatchId: response.data.data.shareBatchId,
        recipientCount: response.data.data.recipientCount,
        shareCount: response.data.data.shares.length,
      });

      toast({ variant: 'success', title: 'Documents shared successfully' });
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message || 'Failed to share documents';
      toast({ variant: 'error', title: msg });
    } finally {
      setIsSubmitting(false);
    }
  }, [documentIds, selectedClients, expiryDays, message, toast]);

  const handleClose = useCallback(() => {
    setSelectedClients([]);
    setExpiryDays(7);
    setMessage('');
    setResult(null);
    onClose();
  }, [onClose]);

  return (
    <>
      <Modal open={open} onClose={handleClose} title="Share Documents" size="lg">
        {result ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
              <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                Shared Successfully
              </h3>
              <p className="mt-2 text-sm text-green-700 dark:text-green-300">
                {result.shareCount} document link(s) sent to {result.recipientCount} recipient(s).
                Links will expire in {expiryDays} day(s).
              </p>
            </div>
            <div className="flex justify-end">
              <Button onClick={handleClose}>Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Selected documents */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Documents to Share ({documentIds.length})
              </label>
              <div className="flex flex-wrap gap-1.5">
                {documentNames.map((name, idx) => (
                  <Badge key={documentIds[idx]} variant="info" size="sm">
                    {name}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Recipient selection */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Share With
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsClientSearchOpen(true)}
                className="w-full justify-start text-gray-500"
              >
                <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                {selectedClients.length > 0
                  ? `${selectedClients.length} client(s) selected — click to change`
                  : 'Search and select clients...'}
              </Button>

              {/* Selected clients display */}
              {selectedClients.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {selectedClients.map((client) => (
                    <Badge key={client.id} variant="default" size="sm">
                      {client.fullName}
                      <button
                        type="button"
                        onClick={() => removeClient(client.id)}
                        className="ml-1 text-gray-400 hover:text-gray-600"
                        aria-label={`Remove ${client.fullName}`}
                      >
                        &times;
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Expiry */}
            <div className="w-40">
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Link Expiry (days)
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={String(expiryDays)}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val >= 1 && val <= 90) {
                    setExpiryDays(val);
                  } else if (e.target.value === '') {
                    setExpiryDays(1);
                  }
                }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              />
              <p className="mt-1 text-xs text-gray-400">1–90 days</p>
            </div>

            {/* Message */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Message (optional)
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Add a message to include in the email..."
                rows={3}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                maxLength={2000}
              />
            </div>

            {/* Actions */}
            <div className="sticky bottom-0 flex justify-end gap-3 border-t border-gray-200 bg-white pt-4 dark:border-gray-700 dark:bg-surface-dark-secondary">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                loading={isSubmitting}
                disabled={selectedClients.length === 0}
              >
                Share & Send Email
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Client Search Modal */}
      {isClientSearchOpen && (
        <ClientSearchModal
          onConfirm={handleClientConfirm}
          onClose={() => setIsClientSearchOpen(false)}
          selectedIds={selectedClients.map((c) => c.id)}
          title="Select Recipients"
        />
      )}
    </>
  );
}
